import {isPlatformBrowser} from '@angular/common';
import {inject, Injector, Injectable, PLATFORM_ID} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {ConversationApiService} from '@shared/services/api/conversation/conversation-api.service';
import {ConversationListService} from './conversation-list.service';
import {IConversation} from '../models/conversation.model';
import {CHANNEL} from '../models/channel.model';
import {ExtensionSendService} from './extension-send.service';

interface IMissingAvatarSyncCacheEntry {
  avatar: string;
  expiresAt: number;
  pageId?: string;
}

/**
 * Service đồng bộ thông tin khách hàng (tên + avatar) từ nền tảng.
 *
 * Flow:
 * 1. Phát hiện tên mặc định ("Facebook User", "Zalo User"...)
 *    hoặc avatar lỗi (403/4xx) → gọi API updateCustomer(syncName: 1)
 * 2. BE fetch tên/avatar thật từ nền tảng (FB, Zalo...)
 * 3. Cập nhật vào conversation list qua ConversationListService
 * 4. List re-render → UI hiển thị tên/avatar thật
 *
 * Chỉ hỗ trợ kênh Facebook và Zalo cá nhân (BE side).
 * Gọi API lần lượt bằng await để tránh áp lực BE.
 */
@Injectable({providedIn: 'root'})
export class CustomerSyncService {
  private readonly api = inject(ConversationApiService);
  private readonly extensionSend = inject(ExtensionSendService);
  private readonly injector = inject(Injector);
  private readonly platformId = inject(PLATFORM_ID);
  private _convListSvc: ConversationListService | null = null;

  /** Lazy get ConversationListService — tránh circular dependency NG0200 */
  private get convListSvc(): ConversationListService {
    if (!this._convListSvc) {
      this._convListSvc = this.injector.get(ConversationListService);
    }
    return this._convListSvc;
  }

  /** Track các conversation đang sync — tránh gọi trùng lặp */
  private _syncing = new Set<string>();
  /** Avatar lỗi có thể xuất hiện ở nhiều vị trí cùng lúc (sidebar, header); chỉ refresh một lần. */
  private readonly _refreshingBrokenAvatars = new Map<string, Promise<string | null>>();
  /**
   * Cache âm cho avatar placeholder/null. Nếu BE vừa sync nhưng vẫn không có
   * avatar thì giữ kết quả đó một ngày, tránh mỗi F5 lại gọi updatecustomer.
   */
  private readonly missingAvatarSyncCacheKey = 'vpage.customer-missing-avatar-sync.v1';
  private readonly missingAvatarSyncTtl = 12 * 60 * 60 * 1000;
  private missingAvatarSyncCache: Record<string, IMissingAvatarSyncCacheEntry> | null = null;

  /** Tên mặc định từ BE khi API nền tảng không lấy được tên thật */
  static readonly DEFAULT_CUSTOMER_NAMES = new Set([
    'Facebook User',
    'Instagram User',
    'Shopee User',
    'Zalo User',
    'Lazada User',
    'Telegram User',
    'WhatsApp User',
    'Whatsapp User',
    'TikTok User',
    'Zalo Personal User',
  ]);

  // ── Detection helpers ─────────────────────────────────────────────

  /** Kiểm tra tên khách có phải là tên mặc định (chưa sync) không */
  isDefaultCustomerName(name: string | null | undefined): boolean {
    if (!name) return false;
    return CustomerSyncService.DEFAULT_CUSTOMER_NAMES.has(name.trim());
  }

  /** Kiểm tra URL avatar có phải là avatar thật (không phải default placeholder) không */
  isRealAvatar(url: string | null | undefined): boolean {
    if (!url) return false;
    return !url.includes('default_avatar') && url.length > 0;
  }

  /**
   * Thiếu tên thật hoặc avatar thật đều cần nhờ BE đồng bộ lại từ platform.
   * Không dùng mỗi tên mặc định: webhook thường đã có tên nhưng vẫn giữ
   * `default_avatar.png` trong DB.
   */
  needsCustomerDataSync(conv: Pick<IConversation, 'id' | 'fanpageUserName' | 'customerAvatar'>): boolean {
    const needsSync = this.isDefaultCustomerName(conv.fanpageUserName)
      || !this.isRealAvatar(conv.customerAvatar);
    return needsSync && !this.isMissingAvatarSyncCached(conv.id, conv.customerAvatar);
  }

  // ── Sync methods ─────────────────────────────────────────────────

  /**
   * Sync thông tin khách hàng (tên + avatar) cho 1 hội thoại.
   * Gọi API updateCustomer(syncName: 1) — BE fetch tên/avatar thật từ nền tảng.
   * Kết quả được cập nhật trực tiếp vào conversation list qua updateConversationInList.
   *
   * @returns { name, avatar } nếu có cập nhật, null nếu không cần sync hoặc lỗi
   */
  async syncCustomerData(
    conv: { id: string; fanpageUserId?: string; fanpageId?: string },
    options?: { originalName?: string },
  ): Promise<{ name?: string; avatar?: string } | null> {
    if (!conv.fanpageUserId || !conv.fanpageId || this._syncing.has(conv.id)) return null;

    this._syncing.add(conv.id);
    let resolvedAvatar = this.toNonEmptyString((conv as {customerAvatar?: unknown}).customerAvatar);
    try {
      const params: Record<string, unknown> = {
        id: conv.id,
        syncName: 1,
        fanpageUserId: conv.fanpageUserId,
        fanpageId: conv.fanpageId,
      };
      if (options?.originalName?.trim()) {
        params['originalName'] = options.originalName.trim();
      }

      const res = await firstValueFrom(this.api.updateCustomer(params));
      // BE trả `{error}` (VD 'No data!' khi Graph không đọc được hồ sơ khách) → không có gì được cập nhật.
      if (res?.code === 0) return null;

      // BE có thể trả trực tiếp `{item, avatar}` hoặc bọc trong `data`.
      // `avatar` cũng có thể là object Graph API (`avatar.data.url`), không
      // phải chuỗi URL trực tiếp.
      const data = (res as any)?.data ?? res;
      const item = data?.item ?? data?.customer ?? data;
      const newName = this.toNonEmptyString(
        item?.name ?? data?.name ?? item?.fanpageUserName ?? data?.fanpageUserName,
      );
      const newAvatar = this.toAvatarUrl(
        item?.avatar ?? data?.avatar ?? item?.customerAvatar ?? data?.customerAvatar,
      );
      resolvedAvatar = newAvatar ?? resolvedAvatar;

      const updates: Partial<IConversation> = {};
      if (newName && typeof newName === 'string' && newName.trim()) {
        updates.fanpageUserName = newName.trim();
      }
      if (newAvatar && typeof newAvatar === 'string' && newAvatar.trim()) {
        updates.customerAvatar = newAvatar.trim();
      }

      if (Object.keys(updates).length > 0) {
        this.convListSvc.updateConversationInList(conv.id, updates);
      }

      return {
        name: updates.fanpageUserName,
        avatar: updates.customerAvatar,
      };
    } catch {
      return null;
    } finally {
      // Chỉ cache khi avatar vẫn placeholder/null. Avatar thật luôn được phép
      // sync lại qua luồng thông thường nếu sau này gặp lỗi tải ảnh.
      if (this.isRealAvatar(resolvedAvatar)) {
        this.clearMissingAvatarSyncCache(conv.id);
      } else {
        this.cacheMissingAvatarSync(conv.id, resolvedAvatar, conv.fanpageId);
      }
      this._syncing.delete(conv.id);
    }
  }

  /**
   * Batch sync tên/avatar placeholder — gọi lần lượt bằng await.
   * Dùng sau khi load danh sách hội thoại lần đầu.
   */
  async syncDefaultNames(convs: IConversation[]): Promise<void> {
    for (const conv of convs) {
      if (!this.needsCustomerDataSync(conv)) continue;
      await this.syncCustomerData(conv);
    }
  }

  private toNonEmptyString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }

  private toAvatarUrl(value: unknown): string | null {
    if (typeof value === 'string') return this.toNonEmptyString(value);
    if (!value || typeof value !== 'object') return null;

    const avatar = value as Record<string, unknown>;
    return this.toNonEmptyString(avatar['url'])
      ?? this.toNonEmptyString((avatar['data'] as Record<string, unknown> | undefined)?.['url']);
  }

  /**
   * Khi người dùng chủ động truy cập/chọn Page, bỏ cache âm của Page đó một
   * lần để có cơ hội lấy avatar mới. Cache các Page khác vẫn được giữ nguyên.
   */
  resetMissingAvatarSyncCacheForPages(pageIds: Array<string | number>): void {
    const selectedPageIds = new Set(pageIds.map(id => String(id)).filter(Boolean));
    if (!selectedPageIds.size) return;

    const cache = this.getMissingAvatarSyncCache();
    let changed = false;
    Object.entries(cache).forEach(([conversationId, entry]) => {
      // Entry đời cũ chưa có pageId cũng được bỏ ở lần chuyển Page đầu tiên;
      // như vậy không để cache legacy chặn cơ hội sync mới.
      if (!entry.pageId || selectedPageIds.has(entry.pageId)) {
        delete cache[conversationId];
        changed = true;
      }
    });
    if (changed) this.persistMissingAvatarSyncCache();
  }

  private isMissingAvatarSyncCached(conversationId: string | undefined, avatar: string | null | undefined): boolean {
    if (!conversationId || this.isRealAvatar(avatar)) return false;
    const cache = this.getMissingAvatarSyncCache();
    const entry = cache[conversationId];
    if (!entry) return false;

    if (entry.expiresAt <= Date.now()) {
      delete cache[conversationId];
      this.persistMissingAvatarSyncCache();
      return false;
    }

    // Cache gắn với cả conversation lẫn giá trị avatar placeholder tại lúc
    // gọi. Nếu BE/extension đã thay giá trị avatar, cho phép thử đồng bộ lại.
    return entry.avatar === this.normalizeAvatarForCache(avatar);
  }

  private cacheMissingAvatarSync(
    conversationId: string,
    avatar: string | null | undefined,
    pageId?: string,
  ): void {
    if (!conversationId || this.isRealAvatar(avatar)) return;
    const cache = this.getMissingAvatarSyncCache();
    cache[conversationId] = {
      avatar: this.normalizeAvatarForCache(avatar),
      expiresAt: Date.now() + this.missingAvatarSyncTtl,
      pageId: pageId ? String(pageId) : undefined,
    };
    this.trimMissingAvatarSyncCache(cache);
    this.persistMissingAvatarSyncCache();
  }

  private clearMissingAvatarSyncCache(conversationId: string): void {
    const cache = this.getMissingAvatarSyncCache();
    if (!cache[conversationId]) return;
    delete cache[conversationId];
    this.persistMissingAvatarSyncCache();
  }

  private getMissingAvatarSyncCache(): Record<string, IMissingAvatarSyncCacheEntry> {
    if (this.missingAvatarSyncCache) return this.missingAvatarSyncCache;

    this.missingAvatarSyncCache = {};
    if (!isPlatformBrowser(this.platformId)) return this.missingAvatarSyncCache;
    try {
      const parsed = JSON.parse(localStorage.getItem(this.missingAvatarSyncCacheKey) || '{}') as Record<string, unknown>;
      if (parsed && typeof parsed === 'object') {
        Object.entries(parsed).forEach(([conversationId, value]) => {
          const item = value as {avatar?: unknown; expiresAt?: unknown; pageId?: unknown};
          const expiresAt = Number(item?.expiresAt);
          if (conversationId && Number.isFinite(expiresAt) && expiresAt > Date.now()) {
            this.missingAvatarSyncCache![conversationId] = {
              avatar: this.normalizeAvatarForCache(item?.avatar),
              expiresAt,
              pageId: typeof item?.pageId === 'string' && item.pageId ? item.pageId : undefined,
            };
          }
        });
      }
    } catch {
      // Local storage hỏng/đầy không được phép chặn việc mở hội thoại.
    }
    return this.missingAvatarSyncCache;
  }

  private persistMissingAvatarSyncCache(): void {
    if (!isPlatformBrowser(this.platformId) || !this.missingAvatarSyncCache) return;
    try {
      localStorage.setItem(this.missingAvatarSyncCacheKey, JSON.stringify(this.missingAvatarSyncCache));
    } catch {
      // Cache là best-effort; không ảnh hưởng luồng đồng bộ chính.
    }
  }

  private trimMissingAvatarSyncCache(cache: Record<string, IMissingAvatarSyncCacheEntry>): void {
    const entries = Object.entries(cache)
      .sort(([, left], [, right]) => left.expiresAt - right.expiresAt);
    // Giới hạn cache để localStorage không phình theo lịch sử hội thoại dài.
    while (entries.length > 500) {
      const [conversationId] = entries.shift()!;
      delete cache[conversationId];
    }
  }

  private normalizeAvatarForCache(avatar: unknown): string {
    return typeof avatar === 'string' ? avatar.trim() : '';
  }

  /**
   * Sync avatar cho 1 hội thoại bị lỗi (403/4xx).
   * Gọi khi image error event fired trên avatar ở sidebar hoặc header hội thoại; các nơi gọi
   * cùng lúc cho một hội thoại dùng chung một request.
   * Instagram cần extension quét lại globalUserId/avatar vì avatar Meta CDN có hạn.
   *
   * @returns URL avatar mới khi BE trả về ngay; null khi không lấy được, kênh không hỗ trợ
   *          hoặc avatar được cập nhật sau qua extension (Instagram)
   */
  syncBrokenAvatar(
    conv: { id: string; channel?: number; fanpageUserId?: string; fanpageId?: string; fanpageUserName?: string; customerAvatar?: string | null }
  ): Promise<string | null> {
    // Chỉ sync nếu avatar là URL thật (không phải default placeholder)
    if (!this.isRealAvatar(conv.customerAvatar)) return Promise.resolve(null);
    const running = this._refreshingBrokenAvatars.get(conv.id);
    if (running) return running;

    // Instagram đi qua extension; các kênh còn lại chỉ FB, Zalo cá nhân, TikTok Business được BE hỗ trợ.
    const isInstagram = conv.channel === CHANNEL.INSTAGRAM;
    if (!isInstagram && ![CHANNEL.FACEBOOK, CHANNEL.ZALO_PERSONAL, CHANNEL.TIKTOK_BUSINESS].includes(conv.channel || -1)) {
      return Promise.resolve(null);
    }

    const request = isInstagram ? this.requestInstagramAvatar(conv) : this.syncAvatarFromPlatform(conv);
    this._refreshingBrokenAvatars.set(conv.id, request);
    return request;
  }

  /**
   * V1 không gọi BE để refresh avatar Instagram. Extension tra lại qua
   * Facebook Page liên kết, kèm forceRescan để nhận profilePicUrl mới.
   */
  private async requestInstagramAvatar(
    conv: { id: string; fanpageUserId?: string; fanpageId?: string; fanpageUserName?: string }
  ): Promise<null> {
    const requested = Boolean(conv.fanpageId && conv.fanpageUserId && await this.extensionSend.requestInstagramGlobalUserId({
      pageId: conv.fanpageId,
      recipientId: conv.fanpageUserId,
      username: conv.fanpageUserName,
      forceRescan: true,
    }));
    // Extension không có callback lỗi. Giữ khoá ngắn để tránh mỗi <img>
    // 403 phát thêm một request, rồi cho phép thử lại nếu extension im lặng.
    setTimeout(() => this._refreshingBrokenAvatars.delete(conv.id), requested ? 30_000 : 0);
    return null;
  }

  private async syncAvatarFromPlatform(
    conv: { id: string; fanpageUserId?: string; fanpageId?: string; customerAvatar?: string | null }
  ): Promise<string | null> {
    try {
      return (await this.syncCustomerData(conv))?.avatar ?? null;
    } finally {
      this._refreshingBrokenAvatars.delete(conv.id);
    }
  }
}
